import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Receipt, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';

type EventType =
  | 'checkout_started'
  | 'trial_started'
  | 'payment_paid'
  | 'payment_failed'
  | 'dunning_retry'
  | 'dunning_recovered'
  | 'dunning_failed'
  | 'canceled';

type PaymentEvent = {
  id: string;
  email: string | null;
  type: EventType;
  status: string | null;
  amountCents: number | null;
  currency: string | null;
  message: string | null;
  createdAt: string;
};

const EVENT_META: Record<EventType, { label: string; cls: string }> = {
  checkout_started:  { label: 'Checkout iniciado', cls: 'bg-slate-100 text-slate-600' },
  trial_started:     { label: 'Prueba iniciada',   cls: 'bg-blue-50 text-blue-700' },
  payment_paid:      { label: 'Cobro OK',          cls: 'bg-emerald-50 text-emerald-700' },
  payment_failed:    { label: 'Cobro fallido',     cls: 'bg-red-50 text-red-700' },
  dunning_retry:     { label: 'Reintento',         cls: 'bg-amber-50 text-amber-700' },
  dunning_recovered: { label: 'Recuperado',        cls: 'bg-emerald-50 text-emerald-700' },
  dunning_failed:    { label: 'Reintento fallido', cls: 'bg-orange-50 text-orange-700' },
  canceled:          { label: 'Cancelada',         cls: 'bg-red-50 text-red-700' },
};

const FILTERS: { id: string; label: string }[] = [
  { id: 'all', label: 'Todos' },
  { id: 'payment_paid', label: 'Cobros OK' },
  { id: 'payment_failed', label: 'Fallos' },
  { id: 'dunning_retry', label: 'Reintentos' },
  { id: 'dunning_failed', label: 'Reintentos fallidos' },
  { id: 'canceled', label: 'Cancelaciones' },
  { id: 'checkout_started', label: 'Checkouts' },
  { id: 'trial_started', label: 'Pruebas' },
];

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function fmtAmount(cents: number | null, currency: string | null): string {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('es-ES', { style: 'currency', currency: currency || 'EUR' });
}

export function AdminPaymentLogs() {
  const [filter, setFilter] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'payment-logs', filter],
    queryFn: () => api<{ events: PaymentEvent[] }>(`/api/admin/payment-logs?type=${filter}`),
    refetchInterval: 30_000,
  });
  const events = data?.events ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Receipt size={22} className="text-brand-600" />
          Logs de pagos
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Actividad de cobros, reintentos y cancelaciones. {events.length} evento{events.length !== 1 ? 's' : ''}.
        </p>
      </div>

      {/* filters */}
      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={cn(
              'text-xs font-medium rounded-full px-3 py-1.5 border transition',
              filter === f.id
                ? 'bg-brand-600 text-white border-brand-600'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-slate-400">
            <Receipt size={28} strokeWidth={1.5} />
            <p className="text-sm">No hay eventos de pago todavía.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Fecha</th>
                <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Evento</th>
                <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider hidden sm:table-cell">Usuario</th>
                <th className="px-5 py-3.5 text-right text-xs font-medium text-slate-400 uppercase tracking-wider hidden md:table-cell">Importe</th>
                <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider hidden lg:table-cell">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => {
                const meta = EVENT_META[e.type] ?? { label: e.type, cls: 'bg-slate-100 text-slate-600' };
                return (
                  <tr key={e.id} className={cn(i < events.length - 1 && 'border-b border-slate-50', 'hover:bg-slate-50 transition-colors')}>
                    <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(e.createdAt)}</td>
                    <td className="px-5 py-3">
                      <span className={cn('inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium', meta.cls)}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 hidden sm:table-cell">
                      <span className="text-slate-700 truncate max-w-[220px] inline-block align-middle">{e.email ?? '—'}</span>
                    </td>
                    <td className="px-5 py-3 text-right text-slate-700 tabular-nums hidden md:table-cell whitespace-nowrap">
                      {fmtAmount(e.amountCents, e.currency)}
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-500 hidden lg:table-cell">{e.message ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
